import React, { memo, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

// helpers
import { getEligibleVariantName } from 'shared/components/ExperimentV2/helpers';
import { log } from 'shared/helpers/newRelic';
import { TAG_IDENTIFIER } from 'shared/helpers/newRelic/constants';
import { getPageType, navigateToURL } from 'shared/components/AppMigration/helpers';

// hooks
import {
  useAppInstallDetection,
  useAppsFlyerSmartScript,
} from 'shared/components/AppMigration/hooks';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

// constants
import {
  APP_MIGRATION_EXP,
  APP_MIGRATION_EXP_VARIANT_A,
  UTM_CAMPAIGN,
  UTM_CONTENT,
} from 'shared/components/AppMigration/constants';

// styles
import {
  BannerContainer,
  LeftSection,
  AppIconContainer,
  TextContent,
  BannerText,
  RightSection,
  CTAButton,
  CloseButton,
} from './styles';

// icons
import { FashionLogoSmall, CloseCircleIcon } from 'shared/components/AppMigration/icons';

// types
import { IAppMigrationBannerProps } from 'shared/components/AppMigration/types';

const TOP_BANNER_DISMISS_KEY = 'app_migration_top_banner_dismissed';
const TOP_BANNER_DISMISS_DURATION_MS = 24 * 60 * 60 * 1000;

const isTopBannerDismissed = (): boolean => {
  if (typeof window === 'undefined') return false;

  try {
    const dismissedTime = localStorage.getItem(TOP_BANNER_DISMISS_KEY);
    if (!dismissedTime) return false;

    const dismissedAt = parseInt(dismissedTime, 10);
    return Date.now() - dismissedAt < TOP_BANNER_DISMISS_DURATION_MS;
  } catch {
    return false;
  }
};

const dismissTopBanner = (): void => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(TOP_BANNER_DISMISS_KEY, Date.now().toString());
  } catch {
    // localStorage may be unavailable
  }
};

function AppMigrationTopBanner({
  className,
  onClose,
  onCTAClick,
  onVisible,
}: IAppMigrationBannerProps) {
  const { pathname } = useLocation();
  const pageType = getPageType(pathname);

  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const appMigrationContent = useRemoteConfig('appMigrationContent');
  const topBannerConfig = appMigrationContent?.topBanner;
  const bannerText = topBannerConfig?.text || 'Install now to get exciting app-only offers';
  const ctaText = topBannerConfig?.ctaText || 'Download App Now!';

  const { isEligible = false, variant = '' } = getEligibleVariantName(APP_MIGRATION_EXP);
  const isFeatureEnabled = isEligible && variant === APP_MIGRATION_EXP_VARIANT_A;

  const { isLoading: isDetecting, isMobile, platform } = useAppInstallDetection();

  const autoLoad = isFeatureEnabled && isMobile;
  const { generateURL } = useAppsFlyerSmartScript({
    autoLoad: autoLoad,
    additionalParams: {
      utmCampaign: UTM_CAMPAIGN,
      utmMedium: pageType,
      utmContent: UTM_CONTENT.STICKY_BANNER,
      trackingCode: variant,
    },
  });

  useEffect(() => {
    if (isDetecting) return;

    if (!isFeatureEnabled) {
      setIsLoading(false);
      return;
    }

    if (!isMobile) {
      setIsLoading(false);
      return;
    }

    if (isTopBannerDismissed()) {
      setIsLoading(false);
      return;
    }

    setIsVisible(true);
    setIsLoading(false);

    if (onVisible) {
      onVisible();
    }
  }, [isDetecting, isMobile, isFeatureEnabled, onVisible]);

  const handleCTAClick = useCallback(async () => {
    try {
      const url = await generateURL({
        page_type: pageType,
        platform: platform,
        deep_link_value: typeof window !== 'undefined' ? window.location.href : '',
        af_dp: typeof window !== 'undefined' ? window.location.href : '',
        af_web_dp: typeof window !== 'undefined' ? window.location.href : '',
      });

      if (onCTAClick) {
        onCTAClick(url);
      }

      navigateToURL(url);
    } catch (error) {
      log({
        errTitle: '[AppMigration] Error generating OneLink URL',
        error,
        tags: { [TAG_IDENTIFIER.MODULE]: 'AppMigrationTopBanner' },
      });
      if (onCTAClick) {
        onCTAClick('');
      }
    }
  }, [generateURL, pageType, platform, onCTAClick]);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    dismissTopBanner();

    if (onClose) {
      onClose();
    }
  }, [onClose]);

  if (typeof window === 'undefined') {
    return null;
  }

  if (isLoading || !isVisible) {
    return null;
  }

  return (
    <BannerContainer className={className} data-testid="app-migration-top-banner">
      <LeftSection>
        <AppIconContainer>
          <FashionLogoSmall />
        </AppIconContainer>

        <TextContent>
          <BannerText>{bannerText}</BannerText>
        </TextContent>
      </LeftSection>

      <RightSection>
        <CTAButton onClick={handleCTAClick} data-testid="app-migration-top-banner-cta">
          {ctaText}
        </CTAButton>

        <CloseButton
          onClick={handleClose}
          aria-label="Close banner"
          data-testid="app-migration-top-banner-close"
        >
          <CloseCircleIcon />
        </CloseButton>
      </RightSection>
    </BannerContainer>
  );
}

export default memo(AppMigrationTopBanner);
